import { checkIfFlagIsToggled } from '@famly/vet_ts-utils_local-storage-config';
import { useKinderConnectCaptureRequiredQuery, useKinderConnectCaptureFlagsQuery, } from '@famly/stat_kinder-connect-capture_data';
import { useIsKinderConnectCaptureEnabled } from './use-is-kinder-connect-capture-enabled';
/**
 * Whether KinderConnect TWC capture is required for a check-in/check-out submission covering the
 * given children -- true if it's required for *any* of them (any-of semantics computed
 * server-side; a bulk action with 3 children where only 1 needs capture still resolves `true`).
 *
 * Skips the fetch entirely while `childIds` is empty -- the backend treats an empty list as a hard
 * error, not `false`, since a subset answer would read the same as "no capture needed".
 *
 * A per-child query error (a stale/unknown child id, a permission gap on one of the children, a
 * network blip, or any other resolver failure) falls back to the site's own capture flag for
 * `siteId` -- the same site-scoped answer every check-in used before this hook existed. This keeps
 * a single resolver failure scoped to "does this site require it" instead of forcing capture at
 * every site for every user. Only when *that* also fails (or no `siteId` is available to fall back
 * to) does the answer fail safe to `true`: erring toward an unnecessary-but-safe capture is
 * preferable to silently letting a check-in through without one it should have had.
 *
 * @param childIds - The children involved in the check-in/check-out action. Pass a one-element
 * array for a single-child entry point.
 * @param siteId - The site the check-in/check-out is happening at, used only as a fallback if the
 * per-child answer errors. Pass an empty string if unavailable -- the fallback is then skipped.
 * @returns Whether KinderConnect capture is enabled for the current user for these children, and
 * whether that answer is still pending -- see `isLoading`.
 */
export const useIsKinderConnectCaptureEnabledForChildren = (childIds, siteId) => {
    const requiredQuery = useKinderConnectCaptureRequiredQuery({
        variables: { childIds },
        skip: childIds.length === 0,
    });
    const requiredQueryErrored = Boolean(requiredQuery.error);
    const siteFallbackAvailable = requiredQueryErrored && Boolean(siteId);
    const siteFallbackQuery = useKinderConnectCaptureFlagsQuery({
        variables: { siteId },
        skip: !siteFallbackAvailable,
    });
    const siteFallbackErrored = Boolean(siteFallbackQuery.error);
    const captureRequired = (() => {
        var _a;
        if (!requiredQueryErrored) {
            return (_a = requiredQuery.data) === null || _a === void 0 ? void 0 : _a.attendance.childCheckins.kinderConnectCaptureRequired;
        }
        if (!siteFallbackAvailable) {
            return true;
        }
        if (siteFallbackQuery.data) {
            return siteFallbackQuery.data.attendance.childCheckins.kinderConnectCapture.captureEnabled;
        }
        if (siteFallbackErrored) {
            return true;
        }
        return undefined; // fallback still in flight
    })();
    const isEnabled = useIsKinderConnectCaptureEnabled(captureRequired);
    const isStillResolving = (!requiredQueryErrored && requiredQuery.loading) || (siteFallbackAvailable && siteFallbackQuery.loading);
    return {
        isEnabled,
        isLoading: isStillResolving && !checkIfFlagIsToggled('KINDER_CONNECT_CAPTURE'),
    };
};
